import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Clock, XCircle, Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loading } from "@/components/auth/RequireAuth";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg, pkr, dateRange } from "@/lib/api";

const ROLE_OPTS = [["organizer", "Organizer", "You design and lead the getaway"], ["operator", "Operator / property", "You run the stay, transport or venue"], ["curator", "Curator", "You facilitate sessions"]];

function ApplyForm({ onDone }) {
  const api = useApi();
  const [f, setF] = useState({ org_name: "", partner_roles: ["organizer"], city: "", phone: "", bio: "" });
  const toggle = (r) => setF({ ...f, partner_roles: f.partner_roles.includes(r) ? f.partner_roles.filter((x) => x !== r) : [...f.partner_roles, r] });
  const submit = async () => {
    try { await api.post("/partner/apply", f); toast.success("Application received. Complete verification next."); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto grid max-w-6xl gap-12 px-5 py-14 md:px-8 lg:grid-cols-[1fr_1.1fr]" data-testid="partner-apply-page">
      <div>
        <div className="eyebrow text-secondary">Partner with GOSHA</div>
        <h1 className="mt-2 text-4xl tracking-tight text-primary sm:text-5xl">Curated, not open publishing.</h1>
        <p className="mt-4 text-muted-foreground">Every partner completes KYC, signs the partner contract and verifies a payout account. Every listing passes a compliance checklist and an ops review before it goes live.</p>
        <img src="https://images.unsplash.com/photo-1755548836772-c3195fa1f326?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Local host" className="mt-8 hidden aspect-[4/3] w-full rounded-2xl object-cover lg:block" />
      </div>
      <div className="space-y-5 rounded-2xl border border-border bg-white p-6 md:p-8">
        <div><Label>Organisation or your name</Label><Input className="mt-2" value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="apply-org-name" /></div>
        <div>
          <Label>Your role(s)</Label>
          <div className="mt-2 space-y-2">
            {ROLE_OPTS.map(([v, l, d]) => (
              <label key={v} className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
                <Checkbox checked={f.partner_roles.includes(v)} onCheckedChange={() => toggle(v)} data-testid={`apply-role-${v}`} />
                <div><div className="text-sm font-medium">{l}</div><div className="text-xs text-muted-foreground">{d}</div></div>
              </label>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>City</Label><Input className="mt-2" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="apply-city" /></div>
          <div><Label>Phone / WhatsApp</Label><Input className="mt-2" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="apply-phone" /></div>
        </div>
        <div><Label>About you</Label><Textarea className="mt-2" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="apply-bio" /></div>
        <Button className="w-full rounded-full" disabled={!f.org_name.trim() || !f.partner_roles.length} onClick={submit} data-testid="apply-submit-btn">Submit application</Button>
      </div>
    </div>
  );
}

const V_FIELDS = {
  kyc: { title: "Identity (KYC)", fields: [["legal_name", "Legal name"], ["cnic", "CNIC / NTN"], ["address", "Registered address"]] },
  contract: { title: "Partner contract", fields: [["signatory", "Authorised signatory"]] },
  bank: { title: "Payout account", fields: [["bank_name", "Bank"], ["account_title", "Account title"], ["iban", "IBAN"]] },
};
const V_ICON = { verified: [BadgeCheck, "text-primary"], submitted: [Clock, "text-secondary"], rejected: [XCircle, "text-destructive"] };

function VerificationCard({ vtype, v, reload }) {
  const api = useApi();
  const cfg = V_FIELDS[vtype];
  const [data, setData] = useState(v?.data || {});
  const [open, setOpen] = useState(false);
  const [Icon, cls] = V_ICON[v?.status] || [FileText, "text-muted-foreground"];
  const submit = async () => {
    try { await api.post(`/partner/verifications/${vtype}`, { data: vtype === "contract" ? { ...data, accepted: true } : data }); toast.success("Submitted for review"); setOpen(false); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid={`verification-card-${vtype}`}>
      <Icon className={`h-5 w-5 ${cls}`} strokeWidth={1.5} />
      <div className="mt-3 font-semibold">{cfg.title}</div>
      <div className="mt-1 text-sm capitalize text-muted-foreground" data-testid={`verification-status-${vtype}`}>{v?.status || "not started"}</div>
      {v?.notes && <p className="mt-1 text-xs text-destructive">{v.notes}</p>}
      {v?.status !== "verified" && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" className="mt-4 rounded-full" data-testid={`verification-open-${vtype}`}>{v ? "Update" : "Start"}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{cfg.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {cfg.fields.map(([k, l]) => <div key={k}><Label>{l}</Label><Input className="mt-1" value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} data-testid={`verification-${vtype}-${k}`} /></div>)}
              {vtype === "contract" && <p className="text-sm text-muted-foreground">By submitting you accept the GOSHA partner terms: GOSHA is merchant of record, payouts release after verified completion, material changes need guest consent.</p>}
              <Button className="w-full rounded-full" onClick={submit} data-testid={`verification-submit-${vtype}`}>Submit</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const STATUS_VARIANT = { published: "default", in_review: "secondary", draft: "outline", rejected: "destructive", completed: "outline" };

function NewListing() {
  const api = useApi();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const create = async () => {
    try { const { data } = await api.post("/partner/listings", { title }); nav(`/partner/listings/${data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild><Button className="rounded-full" data-testid="new-listing-btn"><Plus className="mr-1 h-4 w-4" />New listing</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Start a listing draft</DialogTitle></DialogHeader>
        <Input placeholder="Working title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-listing-title" />
        <Button className="rounded-full" disabled={!title.trim()} onClick={create} data-testid="new-listing-create-btn">Create draft</Button>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard() {
  const api = useApi();
  const [prof, setProf] = useState(null);
  const [listings, setListings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const load = () => {
    api.get("/partner/profile").then((r) => setProf(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/partner/listings").then((r) => setListings(r.data));
    api.get("/partner/payouts").then((r) => setPayouts(r.data));
  };
  useEffect(load, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!prof) return <Loading />;
  const p = prof.profile;
  return (
    <div className="mx-auto max-w-6xl px-5 py-12 md:px-8" data-testid="partner-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow text-secondary">Partner workspace</div>
          <h1 className="mt-2 text-4xl tracking-tight text-primary">{p.org_name}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant={p.verification_status === "verified" ? "default" : "secondary"} data-testid="partner-verification-status">{p.verification_status === "verified" ? "Verified partner" : `Verification ${p.verification_status}`}</Badge>
            {p.partner_roles.map((r) => <Badge key={r} variant="outline" className="capitalize">{r}</Badge>)}
          </div>
        </div>
        <NewListing />
      </div>
      <h2 className="mt-12 text-2xl tracking-tight text-primary">Verification</h2>
      <p className="mt-1 text-sm text-muted-foreground">All three must be verified by GOSHA ops before any listing can be published.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {["kyc", "contract", "bank"].map((t) => <VerificationCard key={t} vtype={t} v={prof.verifications[t]} reload={load} />)}
      </div>
      <h2 className="mt-12 text-2xl tracking-tight text-primary">Listings</h2>
      <div className="mt-5 overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Listing</TableHead><TableHead>Dates</TableHead><TableHead>Status</TableHead><TableHead>Sold</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {listings.length === 0 && <TableRow><TableCell colSpan={5} className="text-muted-foreground">No listings yet.</TableCell></TableRow>}
            {listings.map((l) => (
              <TableRow key={l.id} data-testid={`partner-listing-row-${l.id}`}>
                <TableCell className="font-medium">{l.title}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.start_date ? dateRange(l.start_date, l.end_date) : "—"}</TableCell>
                <TableCell><Badge variant={STATUS_VARIANT[l.status]}>{l.status.replace("_", " ")}</Badge></TableCell>
                <TableCell>{l.seats_sold}/{l.capacity}</TableCell>
                <TableCell className="text-right"><Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`partner-listing-open-${l.id}`}><Link to={`/partner/listings/${l.id}`}>Manage</Link></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <h2 className="mt-12 text-2xl tracking-tight text-primary">Payouts</h2>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {payouts.length === 0 && <p className="text-sm text-muted-foreground">Payouts appear after your first confirmed booking. Funds are held until completion is verified.</p>}
        {payouts.map((po) => (
          <div key={po.id} className="rounded-xl border border-border bg-white p-5" data-testid={`partner-payout-${po.id}`}>
            <div className="flex items-center justify-between"><div className="font-medium">{po.listing?.title}</div><Badge variant="outline" className="capitalize">{po.status}</Badge></div>
            <div className="mt-3 text-sm text-muted-foreground">Gross {pkr(po.gross_pkr)} · commission {po.commission_pct}% · <b className="text-foreground">net {pkr(po.net_pkr)}</b></div>
            <div className="mt-2 text-xs text-muted-foreground">Evidence: partner report {po.evidence.partner_report ? "✓" : "—"} · guest confirmations {po.evidence.guest_confirmations}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PartnerHome() {
  const { me, loading, refresh } = useMe();
  if (loading) return <Loading />;
  if (!me?.partner) return <ApplyForm onDone={refresh} />;
  return <Dashboard />;
}
